/**
 * B3 (E-1.C.2-03): Commission Ledger
 * Admin view. Filter by agency/status/period. Approve/void. Export CSV.
 * Append-only: accrued → payable → paid (never direct update).
 */
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "@/i18n";
import { apiFetch } from "@/lib/api-client";
import { formatCurrency } from "@/lib/i18n";
import { cn } from "@/lib/utils";

interface CommissionEntry {
  id: string;
  orgName: string;
  tenantName: string;
  periodStart: string;
  periodEnd: string;
  grossRevenueCents: number;
  wholesaleCostCents: number;
  marginCents: number;
  commissionCents: number;
  status: "accrued" | "payable" | "paid" | "voided";
  approvedBy: string | null;
  approvedAt: string | null;
}

const STATUS_COLORS: Record<string, string> = {
  accrued: "bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
  payable: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  paid: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
  voided: "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
};

export default function CommissionLedgerPage() {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<CommissionEntry[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [, setIsLoading] = useState(true);

  const loadEntries = useCallback(async () => {
    setIsLoading(true);
    const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
    apiFetch<{ entries: CommissionEntry[] }>(`/platform/billing/commission-ledger${params}`)
      .then((res) => setEntries(res.entries))
      .catch(() => setEntries([]))
      .finally(() => setIsLoading(false));
  }, [statusFilter]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const handleAction = async (id: string, action: "approve" | "void") => {
    await apiFetch(`/platform/billing/commission-ledger/${id}/${action}`, { method: "POST" });
    loadEntries();
  };

  const handleExport = () => {
    const csv = [
      "Agency,Client,Period,Revenue,Cost,Margin,Commission,Status",
      ...entries.map((e) =>
        `${e.orgName},${e.tenantName},${e.periodStart.slice(0, 7)},${e.grossRevenueCents / 100},${e.wholesaleCostCents / 100},${e.marginCents / 100},${e.commissionCents / 100},${e.status}`
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `commission-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmt = (cents: number) => formatCurrency(cents, "EUR");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">{t("billing.commission.title")}</h2>
        <button onClick={handleExport} className="rounded-md border border-border px-3 py-1.5 text-xs text-foreground hover:bg-accent">
          {t("billing.commission.export")}
        </button>
      </div>

      {/* Status filter */}
      <div className="flex gap-2">
        {["all", "accrued", "payable", "paid", "voided"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={cn("rounded-md px-3 py-1.5 text-xs font-medium", statusFilter === s ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-accent")}
          >
            {s === "all" ? t("common.all") : s}
          </button>
        ))}
      </div>

      {/* Ledger table */}
      <div className="rounded-lg border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-muted-foreground">
              <th className="px-3 py-2">{t("billing.commission.agency")}</th>
              <th className="px-3 py-2">{t("billing.commission.client")}</th>
              <th className="px-3 py-2">{t("billing.commission.period")}</th>
              <th className="px-3 py-2 text-right">{t("billing.commission.revenue")}</th>
              <th className="px-3 py-2 text-right">{t("billing.commission.commission")}</th>
              <th className="px-3 py-2">{t("common.status")}</th>
              <th className="px-3 py-2">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {entries.map((e) => (
              <tr key={e.id} className="hover:bg-accent/30">
                <td className="px-3 py-2 text-foreground">{e.orgName}</td>
                <td className="px-3 py-2 text-muted-foreground">{e.tenantName}</td>
                <td className="px-3 py-2 text-muted-foreground">{e.periodStart.slice(0, 7)}</td>
                <td className="px-3 py-2 text-right">{fmt(e.grossRevenueCents)}</td>
                <td className="px-3 py-2 text-right font-medium">{fmt(e.commissionCents)}</td>
                <td className="px-3 py-2">
                  <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", STATUS_COLORS[e.status] ?? "")}>{e.status}</span>
                </td>
                <td className="px-3 py-2 flex gap-1">
                  {e.status === "accrued" && (
                    <button onClick={() => handleAction(e.id, "approve")} className="rounded bg-[var(--color-success)]/10 px-2 py-0.5 text-xs text-[var(--color-success)] hover:bg-[var(--color-success)]/20">
                      {t("billing.commission.approve")}
                    </button>
                  )}
                  {(e.status === "accrued" || e.status === "payable") && (
                    <button onClick={() => handleAction(e.id, "void")} className="rounded bg-[var(--color-destructive)]/10 px-2 py-0.5 text-xs text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/20">
                      {t("billing.commission.void")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
